import type { ICustomerPFForm, ICustomerPJForm } from '@/schemas/customer/customerForm'

/** Opção de lista dos formulários (`{ value, name }`, o formato do Select). */
interface ISelectOption {
    value: string
    name: string
}

/** Constantes do domínio customer (perfil e pedidos). */

/** `type` do cliente: PF no cadastro da loja ("PERSON") e no login social ("Pessoa"); PJ é "COMPANY". */
export const CUSTOMER_TYPE = {
    PERSON: 'PERSON',
    PERSON_SOCIAL_LOGIN: 'Pessoa',
    COMPANY: 'COMPANY',
} as const

/**
 * Regra herdada da tela legada (pedido/[orderNumber]): soma 3 h à `linkCreatedDate` e considera o QR Code vencido
 * depois de 2 h. As 3 h compensam o fuso da data devolvida pelo Maestro (sem offset); a confirmar contra a API.
 */
export const PIX_LINK_TIMEZONE_OFFSET_HOURS = 3
export const PIX_LINK_VALIDITY_HOURS = 2
/** `paymentMethod.name` do PIX (texto do cadastro da forma de pagamento). */
export const PIX_PAYMENT_METHOD_NAME = 'PIX'

/**
 * Campos do ModelState da API -> campos dos formulários de dados do cliente, para o `fieldMap` do useFormAction (os
 * demais erros viram o aviso genérico).
 */
export const CUSTOMER_PF_FIELD_MAP = {
    Email: 'email',
    MainDocNumber: 'cpf',
    Occupation: 'occupation',
} as const satisfies Partial<Record<string, keyof ICustomerPFForm>>

export const CUSTOMER_PJ_FIELD_MAP = {
    Email: 'email',
    MainDocNumber: 'cnpj',
    Name: 'corporateName',
    Occupation: 'occupation',
    InscricaoEstadual: 'inscricaoEstadual',
    InscricaoMunicipal: 'inscricaoMunicipal',
} as const satisfies Partial<Record<string, keyof ICustomerPJForm>>

// ===== OPÇÕES DOS FORMULÁRIOS =====

/** Profissões do cliente pessoa física (campo `occupation` dos formulários de cadastro e de perfil). */
export const CAREER_OPTIONS: ISelectOption[] = [
    { value: 'Agente de saúde', name: 'Agente de saúde' },
    { value: 'Artesão', name: 'Artesão' },
    { value: 'Artista Visual', name: 'Artista Visual' },
    { value: 'Assador', name: 'Assador' },
    { value: 'Barbeiro', name: 'Barbeiro' },
    { value: 'Bartender', name: 'Bartender' },
    { value: 'Biólogo', name: 'Biólogo' },
    { value: 'Biomédico', name: 'Biomédico' },
    { value: 'Body Piercer', name: 'Body Piercer' },
    { value: 'Bombeiro Militar', name: 'Bombeiro Militar' },
    { value: 'Cabeleireiro', name: 'Cabeleireiro' },
    { value: 'Chef/Cozinheiro', name: 'Chef/Cozinheiro' },
    { value: 'Churrasqueiro', name: 'Churrasqueiro' },
    { value: 'Colorista', name: 'Colorista' },
    { value: 'Comerciante', name: 'Comerciante' },
    { value: 'Confeiteiro', name: 'Confeiteiro' },
    { value: 'Cuidador de idosos', name: 'Cuidador de idosos' },
    { value: 'Designer de sobrancelha', name: 'Designer de sobrancelha' },
    { value: 'Dona de casa', name: 'Dona de casa' },
    { value: 'Dentista', name: 'Dentista' },
    { value: 'Enfermeiro', name: 'Enfermeiro' },
    { value: 'Estética Automotiva', name: 'Estética Automotiva' },
    { value: 'Esteticista', name: 'Esteticista' },
    { value: 'Estudante', name: 'Estudante' },
    { value: 'Farmacêutico', name: 'Farmacêutico' },
    { value: 'Fisioterapeuta', name: 'Fisioterapeuta' },
    { value: 'Fonoaudiologo', name: 'Fonoaudiologo' },
    { value: 'Funcionário Público', name: 'Funcionário Público' },
    { value: 'Lash Designer', name: 'Lash Designer' },
    { value: 'Manicure', name: 'Manicure' },
    { value: 'Massoterapeuta', name: 'Massoterapeuta' },
    { value: 'Mecânico', name: 'Mecânico' },
    { value: 'Médico', name: 'Médico' },
    { value: 'Micropigmentador', name: 'Micropigmentador' },
    { value: 'Nail Designer', name: 'Nail Designer' },
    { value: 'Nutricionista', name: 'Nutricionista' },
    { value: 'Pintor', name: 'Pintor' },
    { value: 'Podólogo', name: 'Podólogo' },
    { value: 'Protetico', name: 'Protetico' },
    { value: 'Tatuador', name: 'Tatuador' },
    { value: 'Veterinário', name: 'Veterinário' },
    { value: 'Outros', name: 'Outros' },
]

/** Ramos de atividade do cliente pessoa jurídica (campo `occupation` dos formulários de PJ). */
export const CAREER_PJ_OPTIONS: ISelectOption[] = [
    { value: 'Clínica Médica', name: 'Clínica Médica' },
    { value: 'Clínica Odontológica', name: 'Clínica Odontológica' },
    {
        value: 'Estúdio de tatuagem e piercing',
        name: 'Estúdio de tatuagem e piercing',
    },
    { value: 'Farmácia', name: 'Farmácia' },
    { value: 'Laboratórios', name: 'Laboratórios' },
    { value: 'Órgão Público', name: 'Órgão Público' },
    { value: 'Restaurantes/Cafeterias', name: 'Restaurantes/Cafeterias' },
    {
        value: 'Revendedor de materiais odontológicos',
        name: 'Revendedor de materiais odontológicos',
    },
    {
        value: 'Universidade/Faculdade/Centro de Ensino',
        name: 'Universidade/Faculdade/Centro de Ensino',
    },
    { value: 'Outros', name: 'Outros' },
]

/** UFs do Brasil para o endereço (sigla em `value`). */
export const STATE_OPTIONS: ISelectOption[] = [
    { value: 'AC', name: 'Acre' },
    { value: 'AL', name: 'Alagoas' },
    { value: 'AP', name: 'Amapá' },
    { value: 'AM', name: 'Amazonas' },
    { value: 'BA', name: 'Bahia' },
    { value: 'CE', name: 'Ceará' },
    { value: 'DF', name: 'Distrito Federal' },
    { value: 'ES', name: 'Espírito Santo' },
    { value: 'GO', name: 'Goiás' },
    { value: 'MA', name: 'Maranhão' },
    { value: 'MT', name: 'Mato Grosso' },
    { value: 'MS', name: 'Mato Grosso do Sul' },
    { value: 'MG', name: 'Minas Gerais' },
    { value: 'PA', name: 'Pará' },
    { value: 'PB', name: 'Paraíba' },
    { value: 'PR', name: 'Paraná' },
    { value: 'PE', name: 'Pernambuco' },
    { value: 'PI', name: 'Piauí' },
    { value: 'RJ', name: 'Rio de Janeiro' },
    { value: 'RN', name: 'Rio Grande do Norte' },
    { value: 'RS', name: 'Rio Grande do Sul' },
    { value: 'RO', name: 'Rondônia' },
    { value: 'RR', name: 'Roraima' },
    { value: 'SC', name: 'Santa Catarina' },
    { value: 'SP', name: 'São Paulo' },
    { value: 'SE', name: 'Sergipe' },
    { value: 'TO', name: 'Tocantins' },
]
